import { ActivatedRouteSnapshot, RouterStateSnapshot, Routes } from '@angular/router';
import { canActivateRoleGuard, RoleGuardData, AuthGuard } from '@coin/shared/auth/feature';
import { AuthGmsClientCallbackComponent } from '@coin/shared/auth/feature';
import { PermissionAction, PermissionResource } from '@coin/shared/core/util-enums';
import { environment } from '@coin/shared/core/util-environments';
import { DashboardComponent } from './pages/dashboard/dashboard.component';
import { OrganisationManagementComponent } from './pages/organisation-management/organisation_management.component';
import { RepublishComponent } from './pages/republish/republish.component';
import { RepublishSummaryComponent } from './pages/republish_summary/republish_summary.component';
import { SettingsComponent } from './pages/settings/settings.component';
import { SummaryComponent } from './pages/summary/summary.component';
import { inject } from '@angular/core';
import { of } from 'rxjs';

const APP_PERMISSIONS = {
  superAdmin: {
    resource: PermissionResource.All,
    actions: [PermissionAction.All]
  },
  orgManagementUser: {
    resource: PermissionResource.OrganisationManagementUser,
    actions: [PermissionAction.All]
  },
  orgManagementAdmin: {
    resource: PermissionResource.OrganisationManagementAdmin,
    actions: [PermissionAction.All]
  }
} as const satisfies Record<string, RoleGuardData>;

const getAuthGuards = (...requirements: RoleGuardData[]) => {
  if (!environment.importerAuthentication?.enabled) {
    return () => of(true);
  }
  return canActivateRoleGuard(requirements);
};

export const ROUTES: Routes = [
  {
    path: 'auth/callback',
    component: AuthGmsClientCallbackComponent
  },
  {
    path: '',
    pathMatch: 'full',
    component: DashboardComponent,
    canActivate: [
      (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canActivate(route, state),
      getAuthGuards(APP_PERMISSIONS.superAdmin)
    ]
  },
  {
    path: 'republish',
    pathMatch: 'full',
    component: RepublishComponent,
    canActivate: [
      (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canActivate(route, state),
      getAuthGuards(APP_PERMISSIONS.superAdmin)
    ]
  },
  {
    path: 'settings',
    component: SettingsComponent,
    canActivate: [
      (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canActivate(route, state),
      getAuthGuards(APP_PERMISSIONS.superAdmin)
    ]
  },
  {
    path: 'summary/:id',
    component: SummaryComponent,
    canActivate: [
      (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canActivate(route, state),
      getAuthGuards(APP_PERMISSIONS.superAdmin)
    ]
  },
  {
    path: 'republish_summary/:id',
    component: RepublishSummaryComponent,
    canActivate: [
      (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canActivate(route, state),
      getAuthGuards(APP_PERMISSIONS.superAdmin)
    ]
  },
  {
    path: 'organisation-management',
    component: OrganisationManagementComponent,
    canActivate: [
      (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canActivate(route, state),
      getAuthGuards(APP_PERMISSIONS.superAdmin, APP_PERMISSIONS.orgManagementUser, APP_PERMISSIONS.orgManagementAdmin)
    ]
  },
  {
    path: 'unauthorized',
    loadChildren: () => import('./pages/unauthorized/unauthorized.module').then(m => m.UnauthorizedModule)
  },
  {
    path: 'logout',
    loadChildren: () => import('./pages/log-out/log-out.module').then(m => m.LogOutModule)
  },
  {
    path: '404',
    loadChildren: () => import('./pages/not-found/not-found.module').then(m => m.NotFoundModule)
  },
  {
    path: '**',
    redirectTo: '404'
  }
];
